import React from "react";
import { MdDateRange, MdInsights, MdToday } from "react-icons/md";

function isoTodayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function isoMonthStartLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function shiftDaysIso(baseIso, deltaDays) {
  const base = new Date(`${baseIso}T12:00:00`);
  base.setDate(base.getDate() + deltaDays);
  return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-${String(
    base.getDate(),
  ).padStart(2, "0")}`;
}

const QUICK_RANGES = [
  { key: "today", label: "Hoy", resolve: () => ({ desde: isoTodayLocal(), hasta: isoTodayLocal() }) },
  { key: "7d", label: "7 días", resolve: () => ({ desde: shiftDaysIso(isoTodayLocal(), -6), hasta: isoTodayLocal() }) },
  { key: "30d", label: "30 días", resolve: () => ({ desde: shiftDaysIso(isoTodayLocal(), -29), hasta: isoTodayLocal() }) },
  { key: "mtd", label: "Mes actual", resolve: () => ({ desde: isoMonthStartLocal(), hasta: isoTodayLocal() }) },
];

export default function DashboardDateRangeFloat({
  desde,
  hasta,
  onDesdeChange,
  onHastaChange,
}) {
  const applyQuickRange = React.useCallback(
    (resolver) => {
      const range = resolver();
      onDesdeChange(range.desde);
      onHastaChange(range.hasta);
    },
    [onDesdeChange, onHastaChange],
  );

  return (
    <div className="relative mx-auto -mt-4 mb-5 w-full max-w-5xl px-1 sm:-mt-6 sm:mb-6">
      <div className="group rounded-[26px] border border-gray-300/90 bg-white/95 p-3 shadow-[0_20px_60px_-28px_rgba(15,23,42,0.5)] ring-1 ring-black/5 backdrop-blur transition-all duration-200 dark:border-white/15 dark:bg-navy-900/85 dark:ring-white/10 sm:p-4">
        <div className="hidden items-center justify-between gap-4 lg:flex">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              <MdInsights className="h-4 w-4 text-brand-500 dark:text-brand-300" />
              Ventana de análisis
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-sm font-bold text-navy-700 dark:text-white">
                {desde} → {hasta}
              </span>
              <span className="rounded-full border border-emerald-300/90 bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800 dark:border-emerald-400/25 dark:bg-emerald-400/15 dark:text-emerald-100">
                Rango activo
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-semibold text-gray-500 transition group-hover:text-brand-500 group-focus-within:text-brand-500 dark:text-gray-400 dark:group-hover:text-brand-300 dark:group-focus-within:text-brand-300">
            <MdDateRange className="h-4 w-4" />
            Hover para ajustar
          </div>
        </div>

        <div className="mt-0 max-h-[42rem] overflow-hidden opacity-100 transition-all duration-200 group-hover:mt-4 group-focus-within:mt-4 group-focus-within:opacity-100 group-hover:opacity-100 lg:mt-0 lg:max-h-0 lg:opacity-0 lg:group-hover:mt-4 lg:group-hover:max-h-[42rem] lg:group-hover:opacity-100 lg:group-focus-within:mt-4 lg:group-focus-within:max-h-[42rem] lg:group-focus-within:opacity-100">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400 lg:hidden">
                <MdInsights className="h-4 w-4 text-brand-500 dark:text-brand-300" />
                Ventana de análisis
              </div>
              <h3 className="mt-1 text-sm font-bold text-navy-700 dark:text-white sm:text-base">
                Filtra todo el dashboard desde un solo rango
              </h3>
              <p className="mt-1 max-w-2xl text-xs text-gray-500 dark:text-gray-400">
                Este rango afecta ventas netas, proyección, ticket promedio, top productos,
                incidencias y documentos recientes para revisar el negocio actual rápido.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {QUICK_RANGES.map((range) => (
                <button
                  key={range.key}
                  type="button"
                  onClick={() => applyQuickRange(range.resolve)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-[11px] font-semibold text-gray-700 transition hover:border-brand-200 hover:bg-brand-50 hover:text-brand-600 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:border-brand-400/40 dark:hover:bg-brand-500/10 dark:hover:text-brand-200"
                >
                  {range.key === "today" ? (
                    <MdToday className="h-3.5 w-3.5" />
                  ) : (
                    <MdDateRange className="h-3.5 w-3.5" />
                  )}
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-2.5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300">
              Desde
              <input
                type="date"
                value={desde}
                onChange={(e) => onDesdeChange(e.target.value)}
                className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-navy-700 shadow-sm outline-none transition focus:border-brand-400 focus:ring-2 focus:ring-brand-200/50 dark:border-white/15 dark:bg-navy-800 dark:text-white dark:focus:border-brand-300 dark:focus:ring-brand-500/20"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300">
              Hasta
              <input
                type="date"
                value={hasta}
                onChange={(e) => onHastaChange(e.target.value)}
                className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-navy-700 shadow-sm outline-none transition focus:border-brand-400 focus:ring-2 focus:ring-brand-200/50 dark:border-white/15 dark:bg-navy-800 dark:text-white dark:focus:border-brand-300 dark:focus:ring-brand-500/20"
              />
            </label>
            <div className="rounded-2xl border border-emerald-300/90 bg-emerald-100 px-3 py-2 text-[11px] text-emerald-800 dark:border-emerald-400/25 dark:bg-emerald-400/15 dark:text-emerald-100">
              <div className="font-bold uppercase tracking-wide">Rango activo</div>
              <div className="mt-0.5 font-medium">
                {desde} → {hasta}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
